import * as stylex from '@stylexjs/stylex';
import clsx from 'clsx';
import ReactSelect, { createFilter, components as reactSelectComponents } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { controlStyles } from '../styles/controls';
import { colors } from '../theme/colors.stylex';
import { controlMetrics } from '../theme/controls.stylex';
import type {
  ActionMeta,
  ClassNamesConfig,
  ContainerProps,
  FormatOptionLabelMeta,
  GroupBase,
  MultiValue as MV,
  SingleValue as SV,
  StylesConfig,
} from 'react-select';
import type { Option, SelectProps } from './Select.types';

export type MultiValue<T> = MV<T>;
export type SingleValue<T> = SV<T>;

const styles = stylex.create({
  text: { fontSize: '0.875rem', lineHeight: '1.25rem', color: colors.text },
  container: { pointerEvents: 'auto' },
  control: { borderRadius: controlMetrics.radius, outlineStyle: 'none' },
  regular: { minHeight: controlMetrics.height, paddingRight: '0.375rem', paddingLeft: '0.625rem' },
  small: { minHeight: '22px', paddingRight: '0.25rem', paddingLeft: '0.5rem' },
  open: { borderBottomLeftRadius: 0, borderBottomRightRadius: 0 },
  left: { borderTopRightRadius: 0, borderBottomRightRadius: 0 },
  right: { borderTopLeftRadius: 0, borderBottomLeftRadius: 0 },
  menu: {
    zIndex: 9999,
    minWidth: 'fit-content',
    borderBottomLeftRadius: '0.25rem',
    borderBottomRightRadius: '0.25rem',
    borderWidth: controlMetrics.borderWidth,
    borderStyle: 'solid',
    borderColor: colors.border,
    whiteSpace: 'nowrap',
  },
  menuList: {
    zIndex: 9999,
    minWidth: 'fit-content',
    borderBottomLeftRadius: '0.25rem',
    borderBottomRightRadius: '0.25rem',
    backgroundColor: colors.background,
  },
  menuPortal: { zIndex: 9000 },
  groupHeading: {
    backgroundColor: colors.backgroundDisabled,
    paddingLeft: '0.625rem',
    paddingRight: '0.625rem',
    paddingTop: '0.25rem',
    paddingBottom: '0.25rem',
    color: colors.textMuted,
  },
  option: {
    paddingLeft: '0.625rem',
    paddingRight: '0.625rem',
    paddingTop: '0.25rem',
    paddingBottom: '0.25rem',
    cursor: { 'default': 'default', ':hover': 'pointer' },
    backgroundColor: { 'default': 'transparent', ':hover': colors.menuHover },
  },
  selected: { backgroundColor: { 'default': colors.selectionBackground, ':hover': colors.menuHover } },
  focused: { backgroundColor: { 'default': colors.menuHover, ':hover': colors.menuHover } },
  placeholder: { color: colors.placeholder },
  multiValue: {
    margin: '0.125rem',
    borderRadius: '0.25rem',
    backgroundColor: colors.surfaceSelected,
    paddingLeft: '0.25rem',
    paddingRight: '0.25rem',
    paddingTop: '0.125rem',
    paddingBottom: '0.125rem',
    fontSize: '0.875rem',
    lineHeight: '1.25rem',
  },
  dropdownIndicator: {
    color: {
      'default': colors.border,
      ':hover': colors.borderHover,
      ':focus': colors.borderFocus,
      ':active': colors.borderActive,
    },
  },
  clearIndicator: { color: { 'default': colors.menuTextDisabled, ':hover': colors.borderInvalid } },
  remove: { color: { 'default': 'inherit', ':hover': colors.borderInvalid } },
});

/**
 * react-select does not render unknown props onto the DOM, but it does forward them to selectProps, so this
 * container override reads the testId from there. It must be defined at module level: react-select remounts any
 * slot whose component identity changes between renders, which drops input focus and closes an open menu.
 */
const SelectContainerWithTestId = (containerProps: ContainerProps<Option, boolean, GroupBase<Option>>) => (
  <reactSelectComponents.SelectContainer
    {...containerProps}
    innerProps={
      {
        ...containerProps.innerProps,
        'data-testid': (containerProps.selectProps as { testId?: string }).testId,
      } as typeof containerProps.innerProps
    }
  />
);

/**
 * Select Component
 *  - based on react-select (https://react-select.com/)
 *
 *  For ease of testing most of the elements of this select can be identified by classNames prefixed with "spec".
 *
 */
const Select = ({
  options,
  value,
  placeholder = 'select',
  noOptionsMessage = 'no matching options',
  isSearchable = true,
  creatable = false,
  isMulti = false,
  isClearable = false,
  closeMenuOnSelect = true,
  id,
  inputId,
  menuPortalTarget,
  getOptionLabel,
  getSelectedValueLabel,
  getOptionValue,
  onChange,
  menuIsOpen,
  menuPlacement = 'auto',
  extraClassNames,
  controlClassNames = '',
  xstyle,
  controlXstyle,
  contentXstyle,
  inputGroup,
  filterConfig,
  filterOption,
  small = false,
  isDisabled = false,
  showError = false,
  isLoading = false,
  formatGroupLabel,
  onRemove,
  defaultValue,
  onMenuOpen,
  onMenuClose,
  components: componentsProp,
  onMenuInputFocus,
  onInputChange,
  inputValue,
  onMenuScrollToBottom,
  testId,
}: SelectProps) => {
  // A consumer-provided SelectContainer wins over the testId-injecting one.
  const components = { SelectContainer: SelectContainerWithTestId, ...componentsProp };

  const getOptionOrSelectedLabel = (option: Option, { context }: FormatOptionLabelMeta<Option>) => {
    const getSelectedOptionLabel = getSelectedValueLabel ? getSelectedValueLabel : getOptionLabel;

    if (getOptionLabel || getSelectedValueLabel) {
      if (context === 'value') {
        return getSelectedOptionLabel ? getSelectedOptionLabel(option) : option.label;
      } else {
        return getOptionLabel ? getOptionLabel(option) : option.label;
      }
    } else {
      return option.label;
    }
  };

  // oxlint-disable-next-line typescript/no-redundant-type-constituents
  const interceptOnChange = (newValue: MultiValue<Option> | SingleValue<Option>, actionMeta: ActionMeta<Option>) => {
    if (isMulti && onRemove && actionMeta.action === 'remove-value') {
      onRemove({ id: actionMeta.removedValue });
      return;
    }
    onChange(newValue);
  };

  // react-select defaults blurInputOnSelect to true on touch-capable devices; that can close the menu even when
  // closeMenuOnSelect is false. Only make `blurInputOnSelect=false` when `closeMenuOnSelect=false` (e.g., multi-select
  // where the menu should stay open after each pick), otherwise set to `undefined` to default to the built-in
  // react-select behaviour.
  const blurInputOnSelect = closeMenuOnSelect ? undefined : false;

  const containerProps = stylex.props(styles.text, styles.container, xstyle);
  const menuProps = stylex.props(styles.text, styles.menu, contentXstyle);
  const portalProps = stylex.props(styles.menuPortal, contentXstyle);
  const controlProps = (open: boolean) =>
    stylex.props(
      controlStyles.base,
      styles.control,
      small ? styles.small : styles.regular,
      open && styles.open,
      inputGroup && styles[inputGroup],
      showError && controlStyles.invalid,
      isDisabled && controlStyles.disabled,
      controlXstyle,
    );

  const props = {
    id,
    value,
    inputId,
    placeholder,
    closeMenuOnSelect,
    blurInputOnSelect,
    'isSearchable': creatable ? true : isSearchable,
    isMulti,
    isClearable,
    isLoading,
    defaultValue,
    'noOptionsMessage': () => noOptionsMessage,
    menuPortalTarget,
    menuIsOpen,
    'onChange': interceptOnChange,
    'unstyled': true,
    menuPlacement,
    'formatOptionLabel': getOptionOrSelectedLabel,
    formatGroupLabel,
    getOptionValue,
    options,
    isDisabled,
    'aria-invalid': showError,
    onMenuOpen,
    onMenuClose,
    components,
    // Not a react-select prop; forwarded through selectProps for SelectContainerWithTestId to read.
    testId,
    onMenuInputFocus,
    onInputChange,
    inputValue,
    onMenuScrollToBottom,
    // react-select only fires onMenuScrollToBottom when it captures menu scroll, and captureMenuScroll defaults to
    // false on touch-capable environments — which includes jsdom, which means it would never be triggered. We force it
    // on in tests only; real browsers keep the react-select default.
    // oxlint-disable-next-line no-undef
    'captureMenuScroll': process.env.NODE_ENV === 'test' ? true : undefined,
    'hideSelectedOptions': isMulti,
    'filterOption': filterOption ? filterOption : filterConfig ? createFilter(filterConfig) : undefined,
    // Unstyled react-select still emits structural CSS outside layers. Clear the defaults whose
    // presentation StyleX owns, while retaining its positioning and sizing calculations.
    'styles': {
      container: (base) => ({ ...base, pointerEvents: undefined, ...containerProps.style }),
      control: (base, { menuIsOpen }) => ({
        ...base,
        minHeight: undefined,
        cursor: undefined,
        ...controlProps(menuIsOpen).style,
      }),
      menu: (base) => ({ ...base, zIndex: undefined, ...menuProps.style }),
      menuPortal: (base) => ({ ...base, zIndex: undefined, ...portalProps.style }),
      option: (base) => ({ ...base, cursor: undefined }),
    } satisfies StylesConfig<Option, boolean, GroupBase<Option>>,
    'classNames': {
      control: ({ menuIsOpen }) => clsx(controlProps(menuIsOpen).className, controlClassNames, 'specSelectControl'),
      placeholder: () => clsx(stylex.props(styles.placeholder).className, 'specOpenSelect'),
      container: () => clsx(containerProps.className, extraClassNames),
      input: () => clsx(stylex.props(styles.text).className, 'specSelectInput'),
      menuList: () => clsx(stylex.props(styles.menuList).className, 'specSelectMenu'),
      menu: () => menuProps.className ?? '',
      menuPortal: () => portalProps.className ?? '',
      dropdownIndicator: () => clsx(stylex.props(styles.dropdownIndicator).className, 'specOpenIt'),
      option: ({ isSelected, isDisabled, isFocused }) =>
        clsx(
          stylex.props(styles.option, isSelected ? styles.selected : isFocused && styles.focused).className,
          'specSelectOption',
          isDisabled && 'specDisabledOption',
        ),
      singleValue: () => 'specOpenSelect',
      multiValue: () => clsx(stylex.props(styles.multiValue).className, 'specOpenSelect'),
      multiValueRemove: () => clsx(stylex.props(styles.remove).className, 'specSelectMultiValueRemove'),
      clearIndicator: () => clsx(stylex.props(styles.clearIndicator).className, 'specClearSelect'),
      group: () => 'specSelectGroup',
      groupHeading: () => clsx(stylex.props(styles.groupHeading).className, 'specSelectGroupHeading'),
    } satisfies ClassNamesConfig<Option, boolean, GroupBase<Option>>,
  };

  return creatable ? <CreatableSelect {...props} /> : <ReactSelect {...props} />;
};
export default Select;
